import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHlsPlayer } from '@/hooks/useHlsPlayer';
import { useShakaPlayer } from '@/hooks/useShakaPlayer';
import { useSourceFailover } from '@/hooks/useSourceFailover';
import { useConstant } from '@/hooks/useConstant';
import { useExtraBufferPref, BufferControl } from '@/components/BufferControl';
import { P2pBadge } from '@/components/P2pBadge';
import type { P2pMode } from '@/lib/p2pConfig';
import { buildSportsPlaylistUrl, sportsApi } from '@/services/sportsApi';
import {
  isKnownSportsProvider,
  isDashSportsProvider,
  isEventLive,
  type SportsEvent,
  type SportsProviderName,
} from '@/types/sports';
import { SportsSourceMenu } from '@/components/SportsSourceMenu';

interface Props {
  eventId: string;
}

/**
 * Live-event player. Parallels Watch.tsx (movie/TV/anime) but stripped of
 * features that don't apply to live: no episode picker, no subtitle picker,
 * no resume prompt, no watch-history write, no lobby sync. Reuses
 * useHlsPlayer for the Hls.js lifecycle so improvements there land on both.
 */
export function WatchSports({ eventId }: Props): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const rawSource = searchParams.get('source');
  const activeSource: SportsProviderName | null = isKnownSportsProvider(rawSource) ? rawSource : null;
  // A specific language/quality variant within the active provider. Only
  // meaningful when a provider is pinned — Auto (no ?source=) ignores them.
  const activeSourceId = activeSource ? (searchParams.get('sourceId') || null) : null;
  const rawStreamNo = searchParams.get('streamNo');
  const activeStreamNo =
    activeSource && rawStreamNo && /^\d+$/.test(rawStreamNo) ? Number(rawStreamNo) : null;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [event, setEvent] = useState<SportsEvent | null>(null);
  const [eventError, setEventError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setEventError(null);
    sportsApi.getEvent(eventId)
      .then((res) => { if (!cancelled) setEvent(res.event); })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return;
        if (err.status === 404) {
          navigate('/sports', { replace: true });
          return;
        }
        setEventError(err.message ?? 'Failed to load event');
      });
    return () => { cancelled = true; };
  }, [eventId, navigate]);

  const playlistUrl = useMemo(
    () => buildSportsPlaylistUrl(
      eventId,
      activeSource ?? undefined,
      activeStreamNo ?? undefined,
      activeSourceId ?? undefined,
    ),
    [eventId, activeSource, activeSourceId, activeStreamNo],
  );

  const { value: extraBuffer, setValue: setExtraBuffer } = useExtraBufferPref();
  const failoverBufferSeconds = useConstant('failoverBufferThresholdSeconds');
  const failoverSegment4xx = useConstant('failoverSegment4xxThreshold');
  const failoverWindowMs = useConstant('failoverErrorWindowMs');

  const effectiveSource = useMemo<SportsProviderName | null>(
    () => activeSource ?? event?.sources[0]?.provider ?? null,
    [activeSource, event],
  );

  // DASH (bintvdrm) vs HLS routing. The two paths are mutually exclusive —
  // useShakaPlayer no-ops when `source` is null, and useHlsPlayer's key
  // changes when we swap to/from DASH so it tears down cleanly.
  const isDash = isDashSportsProvider(effectiveSource);

  // P2P swarm for live HLS sports. Skip for DASH — bintvdrm channels are
  // premium per-user feeds; segments are unique per session so a P2P swarm
  // wouldn't share useful bytes between viewers.
  const p2pMode: P2pMode | null = useMemo(() => {
    if (!effectiveSource || isDash) return null;
    return { kind: 'sports', eventId, provider: effectiveSource };
  }, [eventId, effectiveSource, isDash]);

  // HLS path — used by streamed/ppv/sportsrc/daddylive. When the effective
  // source is DASH, pass `playlistUrl: ''` to short-circuit the hook (it
  // returns without constructing an Hls instance).
  const hlsResult = useHlsPlayer({
    playlistUrl: isDash ? '' : playlistUrl,
    videoRef,
    retryToken,
    key: `hls|${eventId}|${activeSource ?? 'auto'}|${activeSourceId ?? ''}|${activeStreamNo ?? ''}|${isDash ? 'inactive' : 'active'}`,
    live: !isDash,
    extraBuffer,
    p2pMode,
  });

  // DASH path — used by bintvdrm. The hook short-circuits when `source` is
  // null OR when the source is not in DASH_SPORTS_PROVIDERS.
  const shakaResult = useShakaPlayer({
    eventId,
    source: isDash ? effectiveSource : null,
    videoRef,
    retryToken,
    key: `shaka|${eventId}|${effectiveSource ?? 'none'}`,
  });

  const fatal = isDash ? shakaResult.fatal : hlsResult.fatal;
  // DASH network failures (Shaka 1001 on a segment) mean the channel's
  // upstream is currently dead/off-air — e.g. an expired DAZN session. The
  // catalog liveness gate hides most of these, but a channel can die between
  // probe and click, so show a friendly message rather than a raw error code.
  const channelUnavailable = isDash && shakaResult.unavailable;
  const hls = isDash ? null : hlsResult.hls;
  const p2pStats = hlsResult.p2pStats;

  const failoverAlternates = useMemo<SportsProviderName[]>(
    () => (event?.sources ?? [])
      .map((s) => s.provider)
      .filter((p): p is SportsProviderName => p !== effectiveSource),
    [event, effectiveSource],
  );

  useSourceFailover({
    hls,
    videoRef,
    currentSource: effectiveSource,
    alternates: failoverAlternates,
    buildUrl: (src) => buildSportsPlaylistUrl(eventId, src as SportsProviderName),
    prefetch: (src) => sportsApi.prefetch(eventId, src as SportsProviderName),
    mediaKind: 'live',
    thresholds: {
      bufferSeconds: failoverBufferSeconds,
      segment4xx: failoverSegment4xx,
      windowMs: failoverWindowMs,
    },
  });

  function onSelectSource(source: SportsProviderName | null): void {
    // Provider-level pick clears any pinned variant → backend picks the
    // provider's first source.
    setSearchParams(
      source ? { source } : {},
      { replace: true },
    );
  }

  function onSelectVariant(
    source: SportsProviderName,
    sourceId: string,
    streamNo?: number,
  ): void {
    const params: Record<string, string> = { source, sourceId };
    if (streamNo !== undefined) params.streamNo = String(streamNo);
    setSearchParams(params, { replace: true });
  }

  if (eventError) {
    return (
      <main className="watch-page">
        <div className="watch-stage__fatal" role="alert">
          <div>{t('watch.sports.loadError', { error: eventError })}</div>
          <button type="button" onClick={() => navigate('/sports')}>{t('watch.sports.backToSports')}</button>
        </div>
      </main>
    );
  }

  return (
    <main className="watch-page watch-page--sports">
      <header className="watch-page__title">
        <h1>{event?.title ?? t('common.loading')}</h1>
        {event && (
          <span className={`time-pill${isEventLive(event) ? ' time-pill--live' : ''}`}>
            {isEventLive(event) ? t('watch.live') : event.sport}
          </span>
        )}
      </header>

      <div className="watch-stage">
        <video
          ref={videoRef}
          controls
          autoPlay
          playsInline
          className="watch-stage__video"
        />
        {fatal && (
          <div
            className="watch-stage__fatal"
            role="alert"
            data-testid="watch-sports-fatal"
          >
            <div>
              {channelUnavailable
                ? t('watch.sports.channelUnavailable')
                : t('watch.sports.streamUnavailable', { source: activeSource ?? 'auto', fatal })}
            </div>
            <button type="button" onClick={() => setRetryToken((n) => n + 1)}>{t('common.retry')}</button>
          </div>
        )}
      </div>

      {event && (
        <SportsSourceMenu
          event={event}
          activeSource={activeSource}
          activeSourceId={activeSourceId}
          activeStreamNo={activeStreamNo}
          onSelect={onSelectSource}
          onSelectVariant={onSelectVariant}
        />
      )}

      <BufferControl value={extraBuffer} onChange={setExtraBuffer} />

      <P2pBadge stats={p2pStats} active={p2pMode != null} />
    </main>
  );
}
